import { ModuleEntity, QuestionsEntity, TargetsEntity } from "../../interfaces";
import { QuestionType, TARGET_COMPLETION_STATUS, _AuthType } from "../../enums";
import {
  AZ_STATE_STANDARD_METADATA,
  CourseMetadata,
  ModuleMetadata,
  OrchardNode,
  QuestionMetadata,
  Skill,
  TargetGroup,
  TargetMetadata,
} from "@backend-types";
import { MacsAuthenticator } from "../../hooks/useMacsAuthenticator";
import axios from "../../services/axios";

type GenericMetadata = any & AZ_STATE_STANDARD_METADATA;

class Node {
  id: string;
  name: string;
  metadata: GenericMetadata;
  parent: Node | null;
  children: any[];
  progress: number;
  order?: number;
  score: number;

  constructor(id: string, name: string, metadata: any, order?: number) {
    this.id = id;
    this.name = name;
    this.children = [];
    this.progress = 0;
    this.score = 0;
    this.metadata = metadata;
    this.parent = null;
    this.order = order;
  }

  setProgress(progress: number) {
    this.progress = progress;
  }

  setScore(average: number) {
    this.score = average;
  }

  getProgress() {
    return this.progress;
  }

  isCompleted() {
    return this.progress === 100;
  }

  isInProgress() {
    return this.progress > 0 && this.progress < 100;
  }

  getMetadata() {
    return this.metadata;
  }

  setMetadata(metadata: GenericMetadata) {
    this.metadata = metadata;
  }

  async updateLinkedAssetProgress(assetId: string, retry?: boolean) {
    let body = {
      linkedAssetId: assetId,
      scoreGiven: this.progress,
    };
    console.warn(
      "COurseTree >>>.",
      MacsAuthenticator.authHook.ltik,
      MacsAuthenticator.authHook.authType,
      _AuthType.LTI
    );
    if (
      MacsAuthenticator.authHook.authType === _AuthType.LTI &&
      MacsAuthenticator.authHook.ltik
    ) {
      let ltik = MacsAuthenticator.authHook.ltik;
      try {
        let response = await axios.post(`/lti/1p3/grade?ltik=${ltik}`, body);
        console.warn("Response from grade endpoint", response);
      } catch (e: any) {
        if (e.response && e.response.status && e.response?.status === 402) {
          console.info("Failed to sync grade, the Canvas assignment is past due");
          //ErrorToast("Failed to sync grade, the Canvas assignment is past due");
        } else if (!retry) {
          console.error("Error while updating target progress, reattempting", e);
          this.updateLinkedAssetProgress(assetId, true);
        }
      }
    } else {
      console.error("Error while updating target progress", "ltik not found");
    }
  }
}

class Course extends Node {
  description: string | null;
  metadata: CourseMetadata;
  higher_ed: boolean;

  constructor(
    id: string,
    name: string,
    description: string | null,
    metadata: CourseMetadata,
    higher_ed: boolean
  ) {
    super(id, name, metadata);
    this.description = description;
    this.metadata = metadata;
    this.higher_ed = higher_ed;
  }

  getAllModules(): Module[] | [] {
    return this.children.filter((child) => child instanceof Module);
  }
}

class Module extends Node {
  landing_page: string;
  landing_page_target: string;
  summary: string;
  feature_flag_id: number | null;
  order: number;
  metadata: ModuleMetadata;
  is_demo_locked: boolean;
  is_asset_locked: boolean;
  target_groups: TargetGroup[];
  course_metadata: CourseMetadata;

  constructor(
    id: string,
    name: string,
    landing_page: string,
    landing_page_target: string,
    summary: string,
    feature_flag_id: number | null,
    order: number,
    metadata: ModuleMetadata,
    parent: Node | null,
    target_groups: TargetGroup[]
  ) {
    super(id, name, metadata);
    this.landing_page = landing_page;
    this.landing_page_target = landing_page_target;
    this.summary = summary;
    this.feature_flag_id = feature_flag_id;
    this.order = order;
    this.parent = parent;
    this.metadata = metadata;
    this.target_groups = target_groups ?? [];

    const courseMetadata = parent?.metadata as CourseMetadata;
    this.course_metadata = courseMetadata;
    const allowedModules = courseMetadata?.demo_overrides?.course_content?.modules;

    this.is_demo_locked = allowedModules ? !allowedModules?.includes(id) : order > 1;
    this.is_asset_locked = true;
  }

  getFormattedModuleName() {
    const showModuleOrder = this.course_metadata?.show_module_order;
    if (showModuleOrder) {
      return `Module ${this.order} - ${this.name}`;
    }
    return this.name;
  }

  getAllSkillIds() {
    let skills: string[] = [];
    this.children.forEach((target) => {
      if (target instanceof Target) {
        let skillIds = target.getAllTargetSkillIds();
        skills = skills.concat(skillIds);
      }
    });
    return skills;
  }

  getFirstTarget(): undefined | Target {
    return this.children.find((child) => child instanceof Target);
  }

  getAllTargets() {
    return this.children.filter((child) => child instanceof Target);
  }

  setModuleLockStatus(lock_status: boolean, overrideLock?: boolean) {
    this.is_asset_locked = overrideLock ? false : lock_status;
  }

  isModuleStarted(): boolean {
    const targets = this.getAllTargets() as Target[];
    return targets.some((t) => t.isTargetStarted() === true);
  }

  async upsertModuleProgress() {
    console.warn("upsertModuleProgress function", this.progress, this.name);
    if (this.progress <= 0) {
      console.warn("Nothing to update because module progress is 0");
    } else {
      let linkedAsset = this.id;
      console.log("linkedAsset", linkedAsset);
      if (linkedAsset) {
        await this.updateLinkedAssetProgress(linkedAsset);
      }
    }
  }
}

class Target extends Node {
  skills_summary: string;
  target_page_summary: string;
  target_completion_status: TARGET_COMPLETION_STATUS;
  module_id: string;
  module_name?: string;
  target_skills: TargetSkill[];
  order: number;
  metadata: TargetMetadata;
  is_demo_locked: boolean;
  is_asset_locked: boolean;
  course_metadata: CourseMetadata;
  constructor(
    id: string,
    name: string,
    skills_summary: string,
    target_page_summary: string,
    module_id: string,
    order: number,
    metadata: TargetMetadata,
    parent: Node | null
  ) {
    super(id, name, metadata);
    this.skills_summary = skills_summary;
    this.target_page_summary = target_page_summary;
    this.target_completion_status = TARGET_COMPLETION_STATUS.NOT_STARTED;
    this.module_id = module_id;
    this.target_skills = [];
    this.parent = parent;
    this.metadata = metadata;
    this.order = order;
    const courseMetadata = parent?.parent?.metadata as CourseMetadata;
    this.course_metadata = courseMetadata;
    const parentModule = parent as Module;

    const allowedTargets = courseMetadata?.demo_overrides?.course_content?.targets;

    this.is_demo_locked = allowedTargets
      ? !allowedTargets?.includes(id)
      : parentModule.is_demo_locked ?? false;
    this.is_asset_locked = true;
  }
  getFormattedTargetName() {
    const showModuleOrder = this.course_metadata?.show_module_order;
    if (showModuleOrder) {
      return `${this.parent?.order}.${this.order} - ${this.name}`;
    }
    return this.name;
  }

  setTargetCompletionStatus(status: TARGET_COMPLETION_STATUS) {
    this.target_completion_status = status;
  }

  getTargetCompletionStatus() {
    return this.target_completion_status;
  }

  getQuestionMetadata(): QuestionMetadata {
    function countNodes(node: OrchardNode, accumulator: QuestionMetadata) {
      const { type, learning_path, supplemental, h5p_type, sendScores } = node;

      const isVideo = type === "play_Video";
      const isQuiz = type === "play_H5P" && h5p_type === "Question Set";

      if (isVideo || isQuiz) {
        if (learning_path === "primary") {
          if (isVideo) {
            accumulator.primary_videos = (accumulator.primary_videos ?? 0) + 1;
          } else if (isQuiz) {
            if (!sendScores) {
              accumulator.primary_quizzes = (accumulator.primary_quizzes ?? 0) + 1;
            } else {
              accumulator.graded_quizzes = (accumulator.graded_quizzes ?? 0) + 1;
            }
          }
        } else if (learning_path === "supplemental") {
          if (isVideo) {
            accumulator.supplementary_videos =
              (accumulator.supplementary_videos ?? 0) + 1;
          } else if (isQuiz) {
            accumulator.supplementary_quizzes =
              (accumulator.supplementary_quizzes ?? 0) + 1;
          }
        }
      }

      if (supplemental && supplemental.length > 0) {
        for (const subNode of supplemental) {
          countNodes(subNode, accumulator);
        }
      }
    }

    return this.getTargetQuestions().reduce<QuestionMetadata>((totals, question) => {
      const metadata = question.metadata as QuestionMetadata | null;
      const root = metadata?.orchardNode;

      if (root) {
        for (const block of root.blocks) {
          countNodes(block, totals);
        }
      }

      return totals;
    }, {});
  }

  getTargetQuestions() {
    return this.children.filter((child) => child instanceof Question);
  }

  getFirstMasteryProblem() {
    return this.children.find(
      (child) => child instanceof Question && child.type === QuestionType.mastery
    );
  }

  getFirstIncompleteQuestion() {
    return this.children.find(
      (child) =>
        child instanceof Question &&
        child.type !== QuestionType.guided &&
        (!child.getQuestionSubmitted() || child.getQuestionRetried())
    );
  }

  getFirstNonPerfectQuestion() {
    return this.children.find(
      (child) =>
        child instanceof Question &&
        child.type !== QuestionType.guided &&
        !child.is_question_perfect
    );
  }

  getFirstQuestion(): undefined | Question {
    return this.children.find(
      (child) => child instanceof Question && child.type !== QuestionType.guided
    );
  }

  getAllTargetSkills() {
    return this.target_skills.sort((a, b) => a.order - b.order) || [];
  }

  getAllTargetSkillIds() {
    return this.getAllTargetSkills().map((skill: TargetSkill) => skill.id);
  }

  isTargetStarted(): boolean {
    const questions = this.getTargetQuestions() as Question[];
    return questions?.some((q: any) => q?.is_question_started === true) ?? false;
  }

  isLastQuestionOfTarget(questionId: string | null | undefined) {
    if (!questionId) {
      return false;
    }
    // return true if current question is last index of target
    const questions = this.getTargetQuestions();
    const questionIndex = questions.findIndex((question) => question.id === questionId);
    return questionIndex === questions.length - 1;
  }

  getFirstPracticeProblem() {
    return this.children.find(
      (child) => child instanceof Question && child.question_number === 1001
    );
  }

  setTargetLockStatus(lock_status: boolean, overrideLock?: boolean) {
    this.is_asset_locked = overrideLock ? false : lock_status;
  }

  async upsertTargetProgress() {
    console.warn("upsertTargetProgress function", this.progress, this.name);
    if (this.progress <= 0) {
      console.warn("Nothing to update because target progress is 0");
    } else {
      console.log(">>>>>>>>>>>>this", this);
      let linkedAsset = this.getFirstPracticeProblem();
      console.log("linkedAsset", linkedAsset);
      if (linkedAsset) {
        await this.updateLinkedAssetProgress(linkedAsset.id);
        // let body = {
        //   linkedAssetId: linkedAsset.id,
        //   scoreGiven: this.progress,
        // };
        // console.warn(
        //   "COurseTree >>>.",
        //   MacsAuthenticator.authHook.ltik,
        //   MacsAuthenticator.authHook.authType,
        //   _AuthType.LTI
        // );
        // if (MacsAuthenticator.authHook.authType === _AuthType.LTI && MacsAuthenticator.authHook.ltik) {
        //   let ltik = MacsAuthenticator.authHook.ltik;
        //   try {
        //     let response = await axios.post(`/lti/1p3/grade?ltik=${ltik}`, body);
        //     console.warn("Response from grade endpoint", response);
        //   } catch (e) {
        //     console.error("Error while updating target progress", e);
        //   }
        // } else {
        //   console.error("Error while updating target progress", "ltik not found");
        // }
      }
    }
  }
}

class TargetSkill extends Node {
  target_id: string;
  content: string;
  title: string;
  skill_id: string;
  order: number;

  constructor(
    id: string,
    title: string,
    videoUrl: string,
    target_id: string,
    metadata: any,
    order: number,
    parent: Node | null
  ) {
    super(id, title, metadata);
    this.skill_id = id;
    this.target_id = target_id;
    this.title = title;
    this.content = videoUrl;
    this.order = order;
    this.parent = parent;
  }
}

class QuestionSkill extends Node {
  question_id: string;
  content: string;
  title: string;
  skill_id: string;

  constructor(
    id: string,
    title: string,
    videoUrl: string,
    question_id: string,
    metadata: any,
    parent: Node | null
  ) {
    super(id, title, metadata);
    this.skill_id = id;
    this.question_id = question_id;
    this.title = title;
    this.content = videoUrl;
    this.parent = parent;
  }
}

class Question extends Node {
  problem_id: string | null;
  template_id: string | null;
  activity_type: string;
  type: QuestionType | null;
  is_question_submitted: boolean;
  is_question_retried: boolean;
  is_question_started: boolean;
  is_question_perfect: boolean; // progress doesn't always equate to points=maxpoints so this is necessary for some behaviors
  is_algorithmic_practice_problem: boolean;
  is_static_gpp: boolean;
  static_problem_url: string | null;

  graded_quiz_id: string | null;
  graded_quiz_max_points: number | null;

  target_id: string;
  module_id: string;
  question_skills: QuestionSkill[];

  question_number: number; // guided practice will from range [1 - 999] practice problem will be from range [1001 - 1999] and mastery will be from range [2001 - 2999]

  is_demo_locked: boolean;

  constructor(
    id: string,
    name: string,
    problem_id: string | null,
    template_id: string | null,
    activity_type: string,
    type: QuestionType | null,
    target_id: string,
    module_id: string,
    question_number: number,
    metadata: any,
    parent: Node | null,
    graded_quiz_id: string | null,
    graded_quiz_max_points: number | null
  ) {
    super(id, name, metadata);
    this.problem_id = problem_id;
    this.template_id = template_id;
    this.activity_type = activity_type;
    this.type = type;
    this.is_question_submitted = false;
    this.is_question_retried = false;
    this.is_question_started = false;
    this.is_question_perfect = false;
    this.target_id = target_id;
    this.module_id = module_id;
    this.question_number = question_number;
    this.question_skills = [];
    this.is_algorithmic_practice_problem =
      template_id && !problem_id && type === QuestionType.practice ? true : false;
    this.is_static_gpp = !template_id && !problem_id && type === QuestionType.guided;
    this.static_problem_url =
      !template_id && !problem_id && type === QuestionType.guided
        ? `https://static-guided-practice.s3.amazonaws.com/${id}/index.html`
        : null;
    this.graded_quiz_id = graded_quiz_id;
    this.graded_quiz_max_points = graded_quiz_max_points;
    this.parent = parent;

    if (parent && parent instanceof Target) {
      this.is_demo_locked = parent.is_demo_locked;
    } else {
      this.is_demo_locked = false;
    }
  }

  setQuestionSubmitted(isSubmitted: boolean) {
    this.is_question_submitted = isSubmitted;
  }

  getQuestionSubmitted() {
    return this.is_question_submitted;
  }

  setQuestionRetried(isRetried: boolean) {
    this.is_question_retried = isRetried;
  }

  getQuestionRetried() {
    return this.is_question_retried;
  }

  setQuestionStarted(isStarted: boolean) {
    this.is_question_started = isStarted;
  }

  setQuestionPerfect(isPerfect: boolean) {
    this.is_question_perfect = isPerfect;
  }

  getQuestionNumber() {
    if (this.type === QuestionType.guided) {
      return this.question_number;
    } else if (this.type === QuestionType.practice) {
      return this.question_number - 1000;
    } else if (this.type === QuestionType.mastery) {
      return this.question_number - 2000;
    } else {
      return 9999;
    }
  }
}

class CourseTree {
  root: Node;

  constructor() {
    this.root = {} as Node;
  }

  findNode(id: string, node: Node): Node | null {
    if (node.id === id) {
      return node;
    }
    for (let i = 0; i < node.children.length; i++) {
      const result = this.findNode(id, node.children[i]);
      if (result) {
        return result;
      }
    }
    return null;
  }

  addCourse(
    id: string,
    name: string,
    description: string | null,
    metadata: CourseMetadata,
    higher_ed: boolean
  ) {
    this.root = new Course(id, name, description, metadata, higher_ed);
  }

  addModule(
    {
      id,
      name,
      landing_page,
      landing_page_target,
      summary,
      feature_flag_id,
      order,
      metadata,
      target_groups,
    }: ModuleEntity,
    courseId: string
  ) {
    let course = this.findNode(courseId, this.root);
    let module = new Module(
      id,
      name,
      landing_page,
      landing_page_target,
      summary,
      feature_flag_id,
      order,
      metadata,
      course,
      target_groups
    );
    console.log(module);
    if (course && course instanceof Course) course.children.push(module);
  }

  addTarget(
    { id, name, skills_summary, target_page_summary, order, metadata }: TargetsEntity,
    moduleId: string
  ) {
    let module = this.findNode(moduleId, this.root);
    let target = new Target(
      id,
      name,
      skills_summary,
      target_page_summary,
      moduleId,
      order,
      metadata,
      module
    );
    if (module && module instanceof Module) module.children.push(target);
  }

  addTargetSkill({ skill_id, title, content }: Skill, targetId: string, order: number) {
    let target = this.findNode(targetId, this.root);
    let targetSkill = new TargetSkill(
      skill_id,
      title,
      content,
      targetId,
      {},
      order,
      target
    );
    if (target && target instanceof Target) target.target_skills.push(targetSkill);
  }

  getTargetSkills(targetId: string) {
    let target = this.findNode(targetId, this.root);
    if (target && target instanceof Target) return target.getAllTargetSkills();
    return [];
  }

  addQuestion(
    {
      id,
      resource_link_title,
      problem_id,
      template_id,
      activity_type,
      type,
      metadata,
      graded_quiz_id,
      graded_quiz_max_points,
    }: QuestionsEntity,
    targetId: string,
    moduleId: string,
    questionNumber: number
  ) {
    let target = this.findNode(targetId, this.root);
    let question = new Question(
      id,
      resource_link_title,
      problem_id,
      template_id,
      activity_type,
      type,
      targetId,
      moduleId,
      questionNumber,
      metadata,
      target,
      graded_quiz_id,
      graded_quiz_max_points
    );
    if (target && target instanceof Target) target.children.push(question);
  }

  addQuestionSkill({ skill_id, title, content }: Skill, questionId: string) {
    let question = this.findNode(questionId, this.root);
    let questionSkill = new QuestionSkill(
      skill_id,
      title,
      content,
      questionId,
      {},
      question
    );
    if (question && question instanceof Question)
      question.question_skills.push(questionSkill);
  }

  getCurrentCourseMetadata() {
    const course = this.root as Course;
    return course.metadata;
  }

  setCurrentCourseMetadata(metadata: CourseMetadata) {
    const course = this.root as Course;
    course.setMetadata(metadata);
  }

  getCurrentCourse() {
    return this.root as Course;
  }

  getQuestionSkills(questionId: string) {
    let question = this.findNode(questionId, this.root);
    if (question && question instanceof Question) return question.question_skills;
    return [];
  }

  getCourse(id: string) {
    const courseNode = this.findNode(id, this.root);
    if (courseNode && courseNode instanceof Course) {
      return courseNode;
    }
    return null;
  }

  getModule(id: string) {
    const moduleNode = this.findNode(id, this.root);
    if (moduleNode && moduleNode instanceof Module) {
      return moduleNode;
    }
    return null;
  }

  getTarget(id: string) {
    const targetNode = this.findNode(id, this.root);
    if (targetNode && targetNode instanceof Target) {
      return targetNode;
    }
    return null;
  }

  getQuestion(id: string) {
    const questionNode = this.findNode(id, this.root);
    if (questionNode && questionNode instanceof Question) {
      return questionNode;
    }
    return null;
  }

  getAllModulesInCourse() {
    let modules: Module[] = [];
    const course = this.root;
    if (course && course instanceof Course) {
      course.children.forEach((module) => {
        if (module instanceof Module) {
          modules.push(module);
        }
      });
    }
    return modules;
  }

  getAllModuleIdsInCourse() {
    let moduleIds = this.getAllModulesInCourse().map((module) => module.id);
    return moduleIds;
  }

  getAllTargetsInCourse() {
    let targets: Target[] = [];
    const course = this.root;
    if (course && course instanceof Course) {
      course.children.forEach((module) => {
        if (module instanceof Module) {
          module.children.forEach((target) => {
            if (target instanceof Target) {
              target.module_name = module.getFormattedModuleName();
              targets.push(target);
            }
          });
        }
      });
    }
    return targets;
  }

  getFirstModuleInCourse(): Module | null {
    const course = this.root;
    if (course && course instanceof Course) {
      return course.children[0];
    }
    return null;
  }

  getFirstTargetInCourse() {
    const module = this.getFirstModuleInCourse();
    if (module) {
      return module.children[0];
    }
    return null;
  }

  getFirstQuestionInCourse() {
    const target = this.getFirstTargetInCourse();
    if (target) {
      return target.children[0];
    }
    return null;
  }

  getAllTargetsInModule(moduleId: string) {
    let targets: Target[] = [];
    const module = this.getModule(moduleId);
    if (module && module instanceof Module) {
      module.children.forEach((target) => {
        if (target instanceof Target) {
          targets.push(target);
        }
      });
    }
    return targets;
  }

  getAllQuestionsInTarget(targetId: string) {
    let questions: Question[] = [];
    const target = this.getTarget(targetId);
    if (target && target instanceof Target) {
      target.children.forEach((question) => {
        if (question instanceof Question) {
          questions.push(question);
        }
      });
    }
    return questions;
  }

  getAllQuestionsInCourse() {
    let questions: Question[] = [];
    const course = this.root;
    if (course && course instanceof Course) {
      course.children.forEach((module) => {
        if (module instanceof Module) {
          module.children.forEach((target) => {
            if (target instanceof Target) {
              target.children.forEach((question) => {
                if (question instanceof Question) {
                  questions.push(question);
                }
              });
            }
          });
        }
      });
    }
    return questions;
  }

  calculateTargetProgress(targetId: string) {
    const target = this.getTarget(targetId);
    if (target) {
      let progress = 0;
      let perfectProgress = 0;
      let guidedProblemCount = 0;
      let masteryProblemWrong = false;
      let tempProgress = 0;
      for (let question of target.children) {
        if (question instanceof Question) {
          if (question.type === QuestionType.guided) {
            guidedProblemCount++;
            continue;
          }
          if (question.type === QuestionType.mastery) {
            if (question.progress === 100) {
              progress = 100;
              target.setProgress(100);
              target.setTargetCompletionStatus(TARGET_COMPLETION_STATUS.MASTERED);
              return progress;
            } else {
              masteryProblemWrong =
                question.is_question_submitted && !question.is_question_retried;
            }
          }
          if (question.type === QuestionType.practice) {
            progress += question.progress;
            if (question.is_question_perfect) {
              perfectProgress += question.progress;
            }
          }
          if (question.is_question_started) {
            tempProgress += -1;
          }
        }
      }
      let maxTargetProgress = 100 * (target.children.length - guidedProblemCount);
      let finalTargetProgress = progress / (target.children.length - guidedProblemCount);

      if (progress === maxTargetProgress && !target.getFirstMasteryProblem()) {
        // if no mastery but all the PPs are finished, should count as mastered target if all problems perfect
        // if all problems not perfect should be needs practice
        if (perfectProgress === maxTargetProgress) {
          target.setTargetCompletionStatus(TARGET_COMPLETION_STATUS.MASTERED);
        } else {
          target.setTargetCompletionStatus(TARGET_COMPLETION_STATUS.NEEDS_PRACTICE);
        }
      } else if (finalTargetProgress < maxTargetProgress && masteryProblemWrong) {
        target.setTargetCompletionStatus(TARGET_COMPLETION_STATUS.NEEDS_PRACTICE);
      } else if (tempProgress < 0) {
        target.setTargetCompletionStatus(TARGET_COMPLETION_STATUS.IN_PROGRESS);
      }
      // biospine override
      // const firstQuestion = target.getFirstQuestion();
      // if (
      //   target.children.length === 1 &&
      //   firstQuestion &&
      //   firstQuestion.activity_type === "orchard_node"
      // ) {
      //   console.log("SETTING TARGET PROGRESS VIA ORCHARD NODE", firstQuestion.progress, finalTargetProgress);
      //   target.setProgress(firstQuestion.progress);
      // } else {
      //   target.setProgress(finalTargetProgress);
      // }
      target.setProgress(finalTargetProgress);
      return finalTargetProgress;
    }
    return 0;
  }

  calculateTargetScore(targetId: string) {
    const target = this.getTarget(targetId);
    if (target) {
      const scoredProblems = target.children.filter(
        (item: Question) =>
          item.type && [QuestionType.practice, QuestionType.mastery].includes(item.type)
      ) as Question[];

      let score = 0;
      for (let question of scoredProblems) {
        if (question instanceof Question) {
          score += question.score;
        }
      }

      const returnedScore = score / scoredProblems.length;
      target.setScore(returnedScore);
      return returnedScore;
    }
    return 0;
  }

  calculateModuleProgress(moduleId: string) {
    const module = this.getModule(moduleId);
    if (module) {
      let progress = 0;
      module.children.forEach((target) => {
        if (target instanceof Target) {
          progress += this.calculateTargetProgress(target.id);
        }
      });
      module.setProgress(progress / module.children.length);
      return progress / module.children.length;
    }
    return 0;
  }

  calculateModuleScore(moduleId: string) {
    const module = this.getModule(moduleId);
    if (module) {
      let score = 0;
      module.children.forEach((target) => {
        if (target instanceof Target) {
          score += this.calculateTargetScore(target.id);
        }
      });
      const returnedScore = score / module.children.length;
      module.setScore(returnedScore);
      return returnedScore;
    }
    return 0;
  }

  calculateCourseProgress() {
    const course = this.root;
    if (course && course instanceof Course) {
      let progress = 0;
      course.children.forEach((module) => {
        if (module instanceof Module) {
          progress += this.calculateModuleProgress(module.id);
        }
      });
      course.setProgress(progress / course.children.length);
      return progress / course.children.length;
    }
    return 0;
  }

  calculateCourseScore() {
    const course = this.root;
    if (course && course instanceof Course) {
      let score = 0;
      course.children.forEach((module) => {
        if (module instanceof Module) {
          score += this.calculateModuleScore(module.id);
        }
      });
      const returnedScore = score / course.children.length;
      course.setScore(returnedScore);
      return returnedScore;
    }
    return 0;
  }

  getModuleByQuestionId(questionId: string) {
    const question = this.getQuestion(questionId);
    if (question) {
      const target = this.getTarget(question.target_id);
      if (target) {
        const module = this.getModule(target.module_id);
        if (module) {
          return module;
        }
      }
    }
    return null;
  }

  getTargetByQuestionId(questionId: string) {
    const question = this.getQuestion(questionId);
    if (question) {
      const target = this.getTarget(question.target_id);
      return target;
    }
    return null;
  }

  hasNextModule(moduleId: string | undefined) {
    if (!moduleId) return false;
    const module = this.getModule(moduleId);
    if (module) {
      const course = this.root;
      if (course && course instanceof Course) {
        const moduleIndex = course.children.findIndex((m) => m.id === module.id);
        if (moduleIndex !== -1 && moduleIndex < course.children.length - 1) {
          return true;
        }
      }
    }
    return false;
  }

  hasNextTarget(targetId: string | undefined) {
    if (!targetId) return false;
    const target = this.getTarget(targetId);
    if (target) {
      const module = this.getModule(target.module_id);
      if (module) {
        const targetIndex = module.children.findIndex((t) => t.id === target.id);
        if (targetIndex !== -1 && targetIndex < module.children.length - 1) {
          return true;
        }
      }
    }
    return false;
  }

  getNextModule(moduleId: string | undefined, demoMode: boolean): Module | null {
    if (!moduleId) return null;

    const module = this.getModule(moduleId);
    if (!module) return null;

    const course = this.root;
    if (!(course && course instanceof Course)) return null;

    const moduleIndex = course.children.findIndex((m) => m.id === module.id);
    if (moduleIndex === -1 || moduleIndex >= course.children.length - 1) return null;

    const nextModule = course.children[moduleIndex + 1];
    if (!(nextModule instanceof Module)) return null;

    if ((nextModule.is_demo_locked && demoMode) || nextModule.is_asset_locked) {
      return null;
    }

    return nextModule;
  }

  getNextTarget(targetId: string | undefined, demoMode: boolean) {
    if (!targetId) return null;
    const target = this.getTarget(targetId);
    if (target) {
      const module = this.getModule(target.module_id);
      if (module) {
        const targetIndex = module.children.findIndex((t) => t.id === target.id);
        if (targetIndex !== -1 && targetIndex < module.children.length - 1) {
          const nextTarget = module.children[targetIndex + 1];
          if (nextTarget.is_demo_locked && demoMode) return null; // prevent going to locked target in demo mode
          return nextTarget;
        }
      }
    }
    return null;
  }

  getNextQuestion(questionId: string | undefined, demoMode: boolean): Question | null {
    if (!questionId) return null;

    const question = this.getQuestion(questionId);
    if (!question) return null;

    const target = this.getTarget(question.target_id);
    if (!target || target.is_asset_locked) return null;

    const module = this.getModule(target.module_id);
    if (!module || module.is_asset_locked) return null;

    const course = this.root instanceof Course ? this.root : null;
    const higherEd = !!(course && (course as any).higher_ed === true);

    // ---------- helpers ----------
    const isPracticeOrMastery = (q: any): q is Question =>
      q instanceof Question &&
      (q.type === QuestionType.practice || q.type === QuestionType.mastery);

    const isPlayable = (q: Question): boolean =>
      isPracticeOrMastery(q) && !(q.is_demo_locked && demoMode);

    const firstPlayableIn = (node: Target | undefined | null): Question | null => {
      if (!node || !(node instanceof Target) || node.is_asset_locked) return null;
      for (const c of node.children) if (c instanceof Question && isPlayable(c)) return c;
      return null;
    };

    const nextSibling = <T>(arr: T[], idx: number): T | null =>
      idx !== -1 && idx < arr.length - 1 ? arr[idx + 1] : null;

    const idxIn = <T extends { id: string }>(arr: T[], id: string) =>
      arr.findIndex((x) => x.id === id);

    const questionIndex = idxIn(target.children as Question[], question.id);
    const targetIndex = idxIn(module.children as Target[], target.id);
    const isLastInTarget = questionIndex === target.children.length - 1;
    const isLastTargetInModule = targetIndex === module.children.length - 1;

    // ---------- STEP 1: next question in same target ----------
    const nextInTarget = nextSibling(target.children as Question[], questionIndex);
    if (
      nextInTarget &&
      nextInTarget instanceof Question &&
      isPlayable(nextInTarget) &&
      !target.is_asset_locked &&
      !module.is_asset_locked
    ) {
      return nextInTarget;
    }

    // ---------- STEP 2: first playable in immediate next target (linear check honored) ----------
    const immediateNextTarget = nextSibling(module.children as Target[], targetIndex);
    if (immediateNextTarget instanceof Target) {
      if (immediateNextTarget.is_asset_locked) {
        if (higherEd) return null; // do not skip past a locked next target
      } else {
        const playable = firstPlayableIn(immediateNextTarget);
        if (playable) return playable;
        if (higherEd) return null; // do not look beyond the immediate next target
      }
    }

    // ---------- STEP 3: backtrack only if last in target AND last target in module ----------
    if (isLastInTarget && isLastTargetInModule) {
      // (a) any incomplete playable question in current target
      if (!target.isCompleted()) {
        for (const c of target.children) {
          if (c instanceof Question && isPracticeOrMastery(c) && !c.isCompleted()) {
            if (c.is_demo_locked && demoMode) return null;
            return c;
          }
        }
      }
      // (b) any incomplete playable question in any unlocked, incomplete target in same module
      if (!module.isCompleted()) {
        for (const t of module.children) {
          if (t instanceof Target && !t.is_asset_locked && !t.isCompleted()) {
            for (const q of t.children) {
              if (q instanceof Question && isPracticeOrMastery(q) && !q.isCompleted()) {
                if (q.is_demo_locked && demoMode) return null;
                return q;
              }
            }
          }
        }
      }
    }

    // ---------- STEP 4: advance to later modules ----------
    if (course) {
      const moduleIndex = idxIn(course.children as Module[], module.id);
      if (moduleIndex !== -1 && moduleIndex < course.children.length - 1) {
        if (higherEd) {
          // Only immediate next module, first target only; no skipping.
          const nextModule = nextSibling(course.children as Module[], moduleIndex);
          if (!(nextModule instanceof Module) || nextModule.is_asset_locked) return null;

          const firstTarget = (nextModule.children || []).find(
            (c) => c instanceof Target
          ) as Target | undefined;
          if (!firstTarget || firstTarget.is_asset_locked) return null;

          for (const q of firstTarget.children) {
            if (q instanceof Question && isPracticeOrMastery(q) && !q.isCompleted()) {
              if (q.is_demo_locked && demoMode) return null;
              return q;
            }
          }
          return null; // first target has no playable question
        }

        // Non-higher-ed: scan forward across unlocked modules/targets
        for (let i = moduleIndex + 1; i < course.children.length; i++) {
          const nextModule = course.children[i];
          if (nextModule instanceof Module && !nextModule.is_asset_locked) {
            for (const t of nextModule.children) {
              if (t instanceof Target && !t.is_asset_locked && !t.isCompleted()) {
                for (const q of t.children) {
                  if (
                    q instanceof Question &&
                    isPracticeOrMastery(q) &&
                    !q.isCompleted()
                  ) {
                    if (q.is_demo_locked && demoMode) return null;
                    return q;
                  }
                }
              }
            }
          }
        }
      }
    }

    return null;
  }

  print() {
    console.log(JSON.stringify(this.root, null, 2));
  }
}

export { CourseTree, Module, Target, Question, Course, TargetSkill };
